import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { UpsertDailyEntryInput } from "@consistency-circle/shared";
import { apiClient } from "../../../lib/api";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";

const RATING_OPTIONS = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
];

const MOOD_LABELS = new Map([
  [1, "Rough"],
  [2, "Low"],
  [3, "Okay"],
  [4, "Good"],
  [5, "Great"],
]);

type DailyEntryMetric = keyof Pick<
  UpsertDailyEntryInput,
  "mood" | "sleepHours" | "sleepQuality" | "dietQuality" | "exerciseQuality"
>;

const EMPTY_ENTRY: UpsertDailyEntryInput = {
  wokeOnTime: false,
  walked: false,
  deepWorkDone: false,
  shutdownNoteDone: false,
  phoneFreeBedtime: false,
  mood: null,
  energy: null,
  focus: null,
  brainFog: null,
  motivation: null,
  sleepHours: null,
  sleepQuality: null,
  dietQuality: null,
  exerciseQuality: null,
  notes: null,
  tomorrowFirstTask: null,
};

export function MoodCheckIn() {
  const queryClient = useQueryClient();
  const [sleepHoursInput, setSleepHoursInput] = useState("");

  const { data: todayEntry } = useQuery({
    queryKey: ["today-entry"],
    queryFn: () => apiClient.getTodayEntry(),
    staleTime: 60 * 1000,
  });

  const metricMutation = useMutation({
    mutationFn: ({ metric, value }: { metric: DailyEntryMetric; value: number | null }) => {
      const existing = todayEntry?.entry;
      const input: UpsertDailyEntryInput = existing
        ? {
            wokeOnTime: existing.wokeOnTime,
            walked: existing.walked,
            deepWorkDone: existing.deepWorkDone,
            shutdownNoteDone: existing.shutdownNoteDone,
            phoneFreeBedtime: existing.phoneFreeBedtime,
            mood: existing.mood,
            energy: existing.energy,
            focus: existing.focus,
            brainFog: existing.brainFog,
            motivation: existing.motivation,
            sleepHours: existing.sleepHours,
            sleepQuality: existing.sleepQuality,
            dietQuality: existing.dietQuality,
            exerciseQuality: existing.exerciseQuality,
            notes: existing.notes,
            tomorrowFirstTask: existing.tomorrowFirstTask,
          }
        : { ...EMPTY_ENTRY };

      input[metric] = value;
      return apiClient.upsertDailyEntry(todayEntry!.localDate, input);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["today-entry"] });
    },
  });

  const entry = todayEntry?.entry;
  const currentMood = entry?.mood ?? null;

  useEffect(() => {
    setSleepHoursInput(
      entry?.sleepHours === null || entry?.sleepHours === undefined ? "" : String(entry.sleepHours),
    );
  }, [entry?.sleepHours]);

  const commitSleepHours = () => {
    const value = sleepHoursInput.trim();
    const nextValue = value === "" ? null : Number(value);

    if ((value !== "" && Number.isNaN(nextValue)) || nextValue === (entry?.sleepHours ?? null)) {
      return;
    }

    metricMutation.mutate({
      metric: "sleepHours",
      value: nextValue,
    });
  };

  if (!todayEntry) return null;

  return (
    <div className="@container space-y-4 rounded-2xl border border-border bg-card p-4">
      <div>
        <p className="text-sm font-medium">Wellness check-in</p>
        <p className="text-xs text-muted-foreground">
          {currentMood
            ? `Feeling ${MOOD_LABELS.get(currentMood)?.toLowerCase()}`
            : "How are you feeling today?"}
        </p>
      </div>

      <div className="space-y-3">
        <RatingRow
          label="How do you feel?"
          metric="mood"
          value={entry?.mood ?? null}
          disabled={metricMutation.isPending}
          onChange={(metric, value) => metricMutation.mutate({ metric, value })}
        />
        <div className="grid gap-3 @sm:grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)]">
          <div className="space-y-1.5">
            <Label htmlFor="sleep-hours" className="text-xs text-muted-foreground">
              Sleep hours
            </Label>
            <Input
              id="sleep-hours"
              type="number"
              min={0}
              max={24}
              step={0.25}
              value={sleepHoursInput}
              placeholder="7.5"
              disabled={metricMutation.isPending}
              onBlur={commitSleepHours}
              onChange={(event) => setSleepHoursInput(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.currentTarget.blur();
                }
              }}
              className="h-9"
            />
          </div>
          <RatingRow
            label="Sleep quality"
            metric="sleepQuality"
            value={entry?.sleepQuality ?? null}
            disabled={metricMutation.isPending}
            onChange={(metric, value) => metricMutation.mutate({ metric, value })}
          />
        </div>
        <RatingRow
          label="Diet"
          metric="dietQuality"
          value={entry?.dietQuality ?? null}
          disabled={metricMutation.isPending}
          onChange={(metric, value) => metricMutation.mutate({ metric, value })}
        />
        <RatingRow
          label="Exercise"
          metric="exerciseQuality"
          value={entry?.exerciseQuality ?? null}
          disabled={metricMutation.isPending}
          onChange={(metric, value) => metricMutation.mutate({ metric, value })}
        />
      </div>
    </div>
  );
}

function RatingRow({
  label,
  metric,
  value,
  disabled,
  onChange,
}: {
  readonly label: string;
  readonly metric: Exclude<DailyEntryMetric, "sleepHours">;
  readonly value: number | null;
  readonly disabled: boolean;
  readonly onChange: (metric: Exclude<DailyEntryMetric, "sleepHours">, value: number) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="min-w-24 text-xs font-medium text-muted-foreground">{label}</span>
      <div className="grid w-full max-w-52 grid-cols-5 gap-1">
        {RATING_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => onChange(metric, option.value)}
            disabled={disabled}
            aria-label={`${label}: ${option.value} out of 5`}
            className={`h-8 rounded-lg border text-xs font-semibold tabular-nums transition-colors ${
              value === option.value
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
            } disabled:opacity-40`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}
